import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Icon,
  InputAdornment,
  Link,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

import { FormHiddenField, IconAutocomplete } from "../../../../components/forms";
import {
  ACTIVITY_PERIOD_OPTIONS,
  DEFAULT_SEVERITY_OPTIONS,
  MEASUREMENT_TYPE_OPTIONS,
  SOURCE_SYSTEM_TYPE_OPTIONS,
  THRESHOLD_TYPE_OPTIONS,
} from "../../../../lib/anomaly/constants";
import {
  buildTimeGateSummary,
  formatCustomerWorkHoursLabel,
  hasCustomerWorkHours,
  isOvernightTimeWindow,
  parseOutsideWorkingHoursFlag,
  resolveCustomerTimeZone,
  toLocalTimeInputValue,
} from "../../../../lib/anomaly/timeGates";
import { DrawerForm, DrawerFormSection } from "../../../../components/DrawerForm";
import { DRAWER_FORM_GRID_SPACING, drawerReadonlyFieldSx } from "../../../../lib/ui/dialogFormLayout";
import type { AnomalyGuide } from "../../../../types/anomalyGuide";
import { customerService, type Customer } from "../../../../services/customerService";
import { anomalyGuideService } from "../../../../services/anomalyGuideService";
import { getCurrentUserRoles, isAdmin } from "../../../../utils/authorization";
import StringListField from "./StringListField";

const readDialogString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const AnomalyEventDialogBody = (item: unknown) => {
  const { t } = useTranslation();
  const dialogItem = (item ?? {}) as Record<string, unknown>;
  const dialogId = typeof dialogItem.id === "string" ? dialogItem.id : "";
  const isEdit = dialogId.length > 0;
  const presetCategory = readDialogString(dialogItem.category);
  const presetSubCategory = readDialogString(dialogItem.subCategory);
  const presetGuidance = readDialogString(dialogItem.classificationGuidance);
  const fromAnomalyGuide =
    dialogItem.fromAnomalyGuide === true || (!isEdit && Boolean(presetCategory && presetSubCategory));

  const [categoryOptions, setCategoryOptions] = useState<string[]>(() =>
    presetCategory ? [presetCategory] : [],
  );
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryGuides, setCategoryGuides] = useState<AnomalyGuide[]>([]);
  const [subCategoryLoading, setSubCategoryLoading] = useState(false);

  const [categoryInput, setCategoryInput] = useState(presetCategory);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(presetCategory || null);
  const [subCategoryInput, setSubCategoryInput] = useState(presetSubCategory);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(presetSubCategory || null);
  const [classificationGuidance, setClassificationGuidance] = useState(presetGuidance);

  const [isAdminUser, setIsAdminUser] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [contextCustomer, setContextCustomer] = useState<Customer | null>(null);
  const [applyToAllCustomers, setApplyToAllCustomers] = useState(false);

  const [selectedSourceSystemType, setSelectedSourceSystemType] = useState<string | null>(null);
  const [selectedActivityPeriod, setSelectedActivityPeriod] = useState<string | null>("Deviation");
  const [selectedThresholdType, setSelectedThresholdType] = useState<string | null>("percent");
  const [threshold, setThreshold] = useState<number>(5);
  const [definition, setDefinition] = useState("");
  const [alertKeywords, setAlertKeywords] = useState<string[]>([]);
  const [correlationFields, setCorrelationFields] = useState<string[]>([]);
  const [selectedMeasurementType, setSelectedMeasurementType] = useState<string | null>(null);
  const [measurementValue, setMeasurementValue] = useState<number>(1);
  const [timeWindowEnabled, setTimeWindowEnabled] = useState(false);
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [outsideWorkingHours, setOutsideWorkingHours] = useState(false);
  const [selectedDefaultSeverity, setSelectedDefaultSeverity] = useState<string | null>(null);

  useEffect(() => {
    void getCurrentUserRoles().then((roles) => setIsAdminUser(isAdmin(roles)));
  }, []);

  const applyCustomerContext = useCallback((customer: Customer | null) => {
    setContextCustomer(customer);
  }, []);

  const loadCustomerById = useCallback(
    async (customerId: string) => {
      try {
        const customer = await customerService.getCustomer(customerId);
        applyCustomerContext(customer ?? null);
        if (customer) {
          setSelectedCustomer(customer);
          setCustomerSearch(customer.name ?? "");
        }
      } catch (error) {
        console.error("Failed to load anomaly customer:", error);
        applyCustomerContext(null);
      }
    },
    [applyCustomerContext],
  );

  useEffect(() => {
    if (isAdminUser) {
      return;
    }

    let active = true;
    void customerService
      .getMyCustomer()
      .then((customer) => {
        if (active) {
          applyCustomerContext(customer);
        }
      })
      .catch((error) => {
        console.error("Failed to load linked customer for time gates:", error);
        if (active) {
          applyCustomerContext(null);
        }
      });

    return () => {
      active = false;
    };
  }, [isAdminUser, applyCustomerContext]);

  const searchCustomers = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setCustomers([]);
      return;
    }

    try {
      setCustomerLoading(true);
      const response = await customerService.getCustomers(0, 20, query);
      setCustomers(response?.content ?? []);
    } catch (error) {
      console.error("Error searching customers:", error);
      setCustomers([]);
    } finally {
      setCustomerLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdminUser || isEdit) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const query = customerSearch.trim();
      if (query.length >= 2 && query !== selectedCustomer?.name) {
        void searchCustomers(query);
      } else if (query.length < 2) {
        setCustomers([]);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [customerSearch, selectedCustomer, searchCustomers, isAdminUser, isEdit]);

  const searchCategories = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setCategoryOptions([]);
      return;
    }

    try {
      setCategoryLoading(true);
      const categories = await anomalyGuideService.searchCategories(query);
      setCategoryOptions(categories);
    } catch (error) {
      console.error("Failed to search categories:", error);
      setCategoryOptions([]);
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (fromAnomalyGuide) {
      return;
    }

    const trimmedQuery = categoryInput.trim();
    if (!trimmedQuery || trimmedQuery === selectedCategory) {
      if (!trimmedQuery) {
        setCategoryOptions([]);
      }
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void searchCategories(categoryInput);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [categoryInput, selectedCategory, searchCategories, fromAnomalyGuide]);

  useEffect(() => {
    if (!selectedCategory) {
      setCategoryGuides([]);
      return;
    }

    let active = true;
    setSubCategoryLoading(true);

    void anomalyGuideService
      .getGuidesByCategory(selectedCategory)
      .then((guides) => {
        if (active) {
          setCategoryGuides(guides);
        }
      })
      .catch((error) => {
        console.error("Failed to load subcategories:", error);
        if (active) {
          setCategoryGuides([]);
        }
      })
      .finally(() => {
        if (active) {
          setSubCategoryLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedCategory]);

  const subCategoryOptions = useMemo(() => {
    const unique = Array.from(
      new Set(categoryGuides.map((guide) => guide.subCategory).filter(Boolean)),
    ) as string[];
    return unique.sort((a, b) => a.localeCompare(b));
  }, [categoryGuides]);

  const filteredSubCategoryOptions = useMemo(() => {
    const query = subCategoryInput.trim().toLowerCase();
    if (!query) {
      return subCategoryOptions;
    }
    return subCategoryOptions.filter((option) => option.toLowerCase().includes(query));
  }, [subCategoryInput, subCategoryOptions]);

  useEffect(() => {
    if (!selectedCategory || !selectedSubCategory) {
      if (!fromAnomalyGuide) {
        setClassificationGuidance("");
      }
      return;
    }

    const match = categoryGuides.find(
      (guide) => guide.category === selectedCategory && guide.subCategory === selectedSubCategory,
    );
    if (match?.classificationGuidance) {
      setClassificationGuidance(match.classificationGuidance ?? "");
      return;
    }

    const existingGuidance =
      typeof dialogItem.classificationGuidance === "string" ? dialogItem.classificationGuidance : "";
    if (fromAnomalyGuide && existingGuidance) {
      setClassificationGuidance(existingGuidance);
      return;
    }

    let active = true;
    void anomalyGuideService
      .lookup(selectedCategory, selectedSubCategory)
      .then((guide) => {
        if (!active) {
          return;
        }
        const guidance =
          guide &&
          typeof guide === "object" &&
          typeof (guide as AnomalyGuide).classificationGuidance === "string"
            ? ((guide as AnomalyGuide).classificationGuidance ?? "")
            : "";
        setClassificationGuidance(guidance);
      })
      .catch((error) => {
        console.error("Failed to load classification guidance:", error);
        if (active) {
          setClassificationGuidance("");
        }
      });

    return () => {
      active = false;
    };
  }, [
    selectedCategory,
    selectedSubCategory,
    categoryGuides,
    fromAnomalyGuide,
    dialogItem.classificationGuidance,
  ]);

  useEffect(() => {
    if (isEdit) {
      return;
    }

    const category = typeof dialogItem.category === "string" ? dialogItem.category : "";
    const subCategory = typeof dialogItem.subCategory === "string" ? dialogItem.subCategory : "";
    const guidance =
      typeof dialogItem.classificationGuidance === "string" ? dialogItem.classificationGuidance : "";

    if (category) {
      setSelectedCategory(category);
      setCategoryInput(category);
    }
    if (subCategory) {
      setSelectedSubCategory(subCategory);
      setSubCategoryInput(subCategory);
    }
    if (guidance) {
      setClassificationGuidance(guidance);
    }
  }, [isEdit, dialogItem.category, dialogItem.subCategory, dialogItem.classificationGuidance]);

  useEffect(() => {
    if (!isEdit) {
      return;
    }

    const category = typeof dialogItem.category === "string" ? dialogItem.category : "";
    const subCategory = typeof dialogItem.subCategory === "string" ? dialogItem.subCategory : "";

    setSelectedCategory(category || null);
    setCategoryInput(category);
    setSelectedSubCategory(subCategory || null);
    setSubCategoryInput(subCategory);
    setSelectedSourceSystemType(
      typeof dialogItem.sourceSystemType === "string" ? dialogItem.sourceSystemType : null,
    );
    setSelectedActivityPeriod(
      typeof dialogItem.activityPeriod === "string"
        ? dialogItem.activityPeriod === "Always"
          ? "Deviation"
          : dialogItem.activityPeriod
        : "Deviation",
    );
    setSelectedThresholdType(
      typeof dialogItem.thresholdType === "string" ? dialogItem.thresholdType : "percent",
    );
    setThreshold(typeof dialogItem.threshold === "number" ? dialogItem.threshold : 5);
    setDefinition(typeof dialogItem.definition === "string" ? dialogItem.definition : "");
    setAlertKeywords(Array.isArray(dialogItem.alertKeywords) ? (dialogItem.alertKeywords as string[]) : []);
    setCorrelationFields(
      Array.isArray(dialogItem.correlationFields) ? (dialogItem.correlationFields as string[]) : [],
    );
    setSelectedMeasurementType(
      typeof dialogItem.measurementType === "string" ? dialogItem.measurementType : null,
    );
    setMeasurementValue(
      typeof dialogItem.measurementValue === "number" && dialogItem.measurementValue > 0
        ? dialogItem.measurementValue
        : 1,
    );

    const nextFrom = toLocalTimeInputValue(
      typeof dialogItem.validFrom === "string" ? dialogItem.validFrom : "",
    );
    const nextTo = toLocalTimeInputValue(
      typeof dialogItem.validTo === "string" ? dialogItem.validTo : "",
    );
    setValidFrom(nextFrom);
    setValidTo(nextTo);
    setTimeWindowEnabled(Boolean(nextFrom && nextTo));
    setOutsideWorkingHours(parseOutsideWorkingHoursFlag(dialogItem.outsideWorkingHours));
    setSelectedDefaultSeverity(
      typeof dialogItem.defaultSeverity === "string" ? dialogItem.defaultSeverity : null,
    );

    if (typeof dialogItem.customerId === "string" && dialogItem.customerId) {
      void loadCustomerById(dialogItem.customerId);
    }
  }, [
    isEdit,
    dialogId,
    dialogItem.category,
    dialogItem.subCategory,
    dialogItem.sourceSystemType,
    dialogItem.activityPeriod,
    dialogItem.thresholdType,
    dialogItem.threshold,
    dialogItem.definition,
    dialogItem.alertKeywords,
    dialogItem.correlationFields,
    dialogItem.measurementType,
    dialogItem.measurementValue,
    dialogItem.validFrom,
    dialogItem.validTo,
    dialogItem.outsideWorkingHours,
    dialogItem.defaultSeverity,
    dialogItem.customerId,
    loadCustomerById,
  ]);

  const workHoursCustomer = selectedCustomer ?? contextCustomer;
  const customerHasWorkHours = hasCustomerWorkHours(workHoursCustomer);
  const customerTimeZone = resolveCustomerTimeZone(workHoursCustomer);
  const timeGateSummary = buildTimeGateSummary({
    validFrom: timeWindowEnabled ? validFrom : "",
    validTo: timeWindowEnabled ? validTo : "",
    outsideWorkingHours,
    customer: workHoursCustomer,
  });
  const showMeasurementFields = selectedActivityPeriod === "Custom";
  const showApplyToAllCustomers = dialogItem.fromAnomalyGuide === true && isAdminUser && !isEdit;

  const handleTimeWindowToggle = (_event: unknown, checked: boolean) => {
    setTimeWindowEnabled(checked);
    if (!checked) {
      setValidFrom("");
      setValidTo("");
    }
  };

  const handleOutsideWorkingHoursToggle = (_event: unknown, checked: boolean) => {
    if (checked && !customerHasWorkHours) {
      return;
    }
    setOutsideWorkingHours(checked);
  };

  return (
    <DrawerForm>
      {isEdit || isAdminUser ? (
      <DrawerFormSection title={t("anomalies.form.customer")}>
      <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
        {isEdit && (
          <Grid size={12}>
            <TextField
              fullWidth
              id="id"
              name="id"
              label={t("anomalies.form.id")}
              variant="outlined"
              size="small"
              value={String(dialogItem.id || "")}
              slotProps={{ input: { readOnly: true } }}
              sx={drawerReadonlyFieldSx}
            />
          </Grid>
        )}

        {isAdminUser && (
          <Grid size={12}>
            {isEdit ? (
              <TextField
                fullWidth
                label={t("anomalies.form.customer")}
                variant="outlined"
                size="small"
                value={selectedCustomer?.name || customerSearch || "—"}
                slotProps={{ input: { readOnly: true } }}
                helperText={t("anomalies.form.customerLocked")}
                sx={drawerReadonlyFieldSx}
              />
            ) : (
              <>
              {showApplyToAllCustomers ? (
                <Box sx={{ mb: 1 }}>
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        checked={applyToAllCustomers}
                        onChange={(_event, checked) => {
                          setApplyToAllCustomers(checked);
                          if (checked) {
                            setSelectedCustomer(null);
                            setCustomerSearch("");
                            setCustomers([]);
                            applyCustomerContext(null);
                          }
                        }}
                      />
                    }
                    label={t("anomalies.form.applyToAllCustomers")}
                  />
                  {applyToAllCustomers ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {t("anomalies.form.applyToAllCustomersHint")}
                    </Typography>
                  ) : null}
                  <FormHiddenField name="applyToAllCustomers" value={applyToAllCustomers ? "true" : ""} />
                </Box>
              ) : null}
              <Autocomplete
                options={customers}
                loading={customerLoading}
                disabled={applyToAllCustomers}
                filterOptions={(options) => options}
                getOptionLabel={(option) => option.name || option.id || ""}
                value={selectedCustomer}
                inputValue={customerSearch}
                onInputChange={(_event, value, reason) => {
                  if (reason === "input") {
                    setCustomerSearch(value);
                    if (value !== selectedCustomer?.name) {
                      setSelectedCustomer(null);
                      applyCustomerContext(contextCustomer && !isAdminUser ? contextCustomer : null);
                    }
                  } else if (reason === "clear") {
                    setCustomerSearch("");
                    setSelectedCustomer(null);
                    setCustomers([]);
                    applyCustomerContext(null);
                  }
                }}
                onChange={(_event, value) => {
                  setSelectedCustomer(value);
                  setCustomerSearch(value?.name ?? "");
                  setCustomers(value ? [value] : []);
                  if (value?.id) {
                    void loadCustomerById(value.id);
                  } else {
                    applyCustomerContext(null);
                    if (outsideWorkingHours) {
                      setOutsideWorkingHours(false);
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required={!applyToAllCustomers}
                    label={t("anomalies.form.customer")}
                    variant="outlined"
                    size="small"
                    placeholder={t("anomalies.form.searchCustomerByName")}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Icon fontSize="small" color="action">
                              business
                            </Icon>
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon fontSize="small" color="action">
                      business
                    </Icon>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.name || t("anomalies.unnamedCustomer")}
                      </Typography>
                      {option.email ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText={
                  customerSearch.trim().length < 2 ? t("common.typeAtLeast2") : t("common.noResults")
                }
              />
              </>
            )}
            <FormHiddenField
              name="customerId"
              value={
                selectedCustomer?.id ||
                (typeof dialogItem.customerId === "string" ? dialogItem.customerId : "") ||
                ""
              }
            />
          </Grid>
        )}

      </Grid>
      </DrawerFormSection>
      ) : null}

      <DrawerFormSection title={t("anomalies.form.sourceSystemType")}>
      <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
        <FormHiddenField
          name="customerStartWorkTime"
          value={toLocalTimeInputValue(workHoursCustomer?.startWorkTime)}
        />
        <FormHiddenField
          name="customerEndWorkTime"
          value={toLocalTimeInputValue(workHoursCustomer?.endWorkTime)}
        />
        <FormHiddenField name="customerTimeZone" value={customerTimeZone} />

        <Grid size={12}>
          <IconAutocomplete
            options={[...SOURCE_SYSTEM_TYPE_OPTIONS]}
            value={selectedSourceSystemType}
            onChange={setSelectedSourceSystemType}
            label={t("anomalies.form.sourceSystemType")}
            required
          />
          <FormHiddenField name="sourceSystemType" value={selectedSourceSystemType || ""} />
        </Grid>

        <Grid size={6}>
          {fromAnomalyGuide ? (
            <>
              <TextField
                required
                fullWidth
                label={t("anomalies.form.category")}
                variant="outlined"
                size="small"
                value={selectedCategory || ""}
                slotProps={{ input: { readOnly: true } }}
                helperText={t("anomalies.form.lockedFromGuide")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: ({ palette }) => palette.background.sectionAlt,
                  },
                }}
              />
              <FormHiddenField name="category" value={selectedCategory || ""} />
            </>
          ) : (
            <>
              <Autocomplete
                freeSolo
                options={categoryOptions}
                loading={categoryLoading}
                inputValue={categoryInput}
                value={selectedCategory}
                onInputChange={(_event, value) => {
                  setCategoryInput(value);
                  if (value !== selectedCategory) {
                    setSelectedCategory(null);
                    setSelectedSubCategory(null);
                    setSubCategoryInput("");
                  }
                }}
                onChange={(_event, value) => {
                  const next = typeof value === "string" ? value : value;
                  setSelectedCategory(next);
                  setCategoryInput(next || "");
                  setSelectedSubCategory(null);
                  setSubCategoryInput("");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={t("anomalies.form.category")}
                    variant="outlined"
                    size="small"
                    placeholder={t("anomalies.form.searchCategory")}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Icon fontSize="small" color="action">
                              category
                            </Icon>
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...rest } = props as typeof props & { key?: string };
                  return (
                    <Box
                      component="li"
                      key={key}
                      {...rest}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Icon fontSize="small" color="action">
                        category
                      </Icon>
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  );
                }}
              />
              <FormHiddenField name="category" value={selectedCategory || categoryInput || ""} />
            </>
          )}
        </Grid>

        <Grid size={6}>
          {fromAnomalyGuide ? (
            <>
              <TextField
                required
                fullWidth
                label={t("anomalies.form.subcategory")}
                variant="outlined"
                size="small"
                value={selectedSubCategory || ""}
                slotProps={{ input: { readOnly: true } }}
                helperText={t("anomalies.form.lockedFromGuide")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: ({ palette }) => palette.background.sectionAlt,
                  },
                }}
              />
              <FormHiddenField name="subCategory" value={selectedSubCategory || ""} />
            </>
          ) : (
            <>
              <Autocomplete
                freeSolo
                options={filteredSubCategoryOptions}
                loading={subCategoryLoading}
                disabled={!selectedCategory}
                inputValue={subCategoryInput}
                value={selectedSubCategory}
                onInputChange={(_event, value) => {
                  setSubCategoryInput(value);
                  if (value !== selectedSubCategory) {
                    setSelectedSubCategory(null);
                  }
                }}
                onChange={(_event, value) => {
                  const next = typeof value === "string" ? value : value;
                  setSelectedSubCategory(next);
                  setSubCategoryInput(next || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={t("anomalies.form.subcategory")}
                    variant="outlined"
                    size="small"
                    placeholder={
                      selectedCategory
                        ? t("anomalies.form.searchSubcategory")
                        : t("anomalies.selectCategoryFirst")
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Icon fontSize="small" color="action">
                              subdirectory_arrow_right
                            </Icon>
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...rest } = props as typeof props & { key?: string };
                  return (
                    <Box
                      component="li"
                      key={key}
                      {...rest}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Icon fontSize="small" color="action">
                        subdirectory_arrow_right
                      </Icon>
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  );
                }}
                noOptionsText={
                  !selectedCategory
                    ? t("anomalies.selectCategoryFirstLong")
                    : subCategoryLoading
                      ? t("anomalies.form.loadingSubcategories")
                      : t("anomalies.form.noSubcategories")
                }
              />
              <FormHiddenField name="subCategory" value={selectedSubCategory || subCategoryInput || ""} />
            </>
          )}
        </Grid>

        {selectedCategory && selectedSubCategory && (
          <Grid size={12}>
            <Accordion
              key={`${selectedCategory}|${selectedSubCategory}`}
              defaultExpanded={false}
              disableGutters
              variant="outlined"
              sx={{
                borderRadius: "4px",
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("anomalies.form.classificationGuidance")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {classificationGuidance || t("anomalies.form.noGuidance")}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

        <Grid size={6}>
          <IconAutocomplete
            options={[...ACTIVITY_PERIOD_OPTIONS]}
            value={selectedActivityPeriod}
            onChange={(next) => {
              setSelectedActivityPeriod(next);
              if (next !== "Custom") {
                setSelectedMeasurementType(null);
                setMeasurementValue(1);
              }
            }}
            label={t("anomalies.form.activityPeriod")}
            required
          />
          <FormHiddenField name="activityPeriod" value={selectedActivityPeriod || ""} />
        </Grid>

        <Grid size={6}>
          <IconAutocomplete
            options={[...THRESHOLD_TYPE_OPTIONS]}
            value={selectedThresholdType}
            onChange={setSelectedThresholdType}
            label={t("anomalies.form.thresholdType")}
            required
          />
          <FormHiddenField name="thresholdType" value={selectedThresholdType || ""} />
        </Grid>

        {showMeasurementFields ? (
          <>
            <Grid size={6}>
              <IconAutocomplete
                options={[...MEASUREMENT_TYPE_OPTIONS]}
                value={selectedMeasurementType}
                onChange={setSelectedMeasurementType}
                label={t("anomalies.form.measurementType")}
                required
              />
              <FormHiddenField name="measurementType" value={selectedMeasurementType || ""} />
            </Grid>
            <Grid size={6}>
              <TextField
                required
                fullWidth
                id="measurementValue"
                name="measurementValue"
                label={t("anomalies.form.measurementValue")}
                type="number"
                variant="outlined"
                size="small"
                value={measurementValue}
                onChange={(event) => setMeasurementValue(parseInt(event.target.value, 10) || 1)}
                slotProps={{ htmlInput: { min: 1 } }}
              />
            </Grid>
          </>
        ) : null}

        <Grid size={6}>
          <TextField
            required
            fullWidth
            id="threshold"
            name="threshold"
            label={t("anomalies.form.threshold")}
            type="number"
            variant="outlined"
            size="small"
            value={threshold}
            onChange={(event) => setThreshold(parseInt(event.target.value, 10) || 1)}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        </Grid>

        <Grid size={6}>
          <IconAutocomplete
            options={[...DEFAULT_SEVERITY_OPTIONS]}
            value={selectedDefaultSeverity}
            onChange={setSelectedDefaultSeverity}
            label={t("anomalies.form.defaultSeverity")}
            placeholder={t("anomalies.form.selectSeverity")}
          />
          <FormHiddenField name="defaultSeverity" value={selectedDefaultSeverity || ""} />
        </Grid>

        <Grid size={12}>
          <Box>
            <Typography
              component="h3"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "text.secondary",
                mb: 0.5,
              }}
            >
              {t("anomalies.form.timeConditions")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("anomalies.form.timeConditionsHint", { timeZone: customerTimeZone })}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={timeWindowEnabled}
                  onChange={handleTimeWindowToggle}
                  size="small"
                />
              }
              label={t("anomalies.form.enableTimeWindow")}
              sx={{ mb: timeWindowEnabled ? 1 : 0.5 }}
            />

            {timeWindowEnabled ? (
              <Grid container spacing={DRAWER_FORM_GRID_SPACING} sx={{ mb: 1 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    id="validFrom"
                    name="validFrom"
                    label={t("anomalies.form.startTime")}
                    type="time"
                    variant="outlined"
                    size="small"
                    value={validFrom}
                    onChange={(event) => setValidFrom(event.target.value)}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 60 } }}
                    helperText={t("anomalies.form.timeWindowEmptyHint")}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    id="validTo"
                    name="validTo"
                    label={t("anomalies.form.endTime")}
                    type="time"
                    variant="outlined"
                    size="small"
                    value={validTo}
                    onChange={(event) => setValidTo(event.target.value)}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 60 } }}
                    helperText={
                      isOvernightTimeWindow(validFrom, validTo)
                        ? t("anomalies.form.overnightHint")
                        : t("anomalies.form.customerTzHint")
                    }
                  />
                </Grid>
              </Grid>
            ) : (
              <>
                <FormHiddenField name="validFrom" value="" />
                <FormHiddenField name="validTo" value="" />
              </>
            )}

            <Tooltip title={customerHasWorkHours ? "" : t("anomalies.form.workHoursRequired")}>
              <span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={outsideWorkingHours}
                      onChange={handleOutsideWorkingHoursToggle}
                      disabled={!customerHasWorkHours}
                      size="small"
                    />
                  }
                  label={t("anomalies.form.outsideWorkingHours")}
                />
              </span>
            </Tooltip>
            <FormHiddenField
              name="outsideWorkingHours"
              value={outsideWorkingHours ? "true" : "false"}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {formatCustomerWorkHoursLabel(workHoursCustomer)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <Link component={RouterLink} to="/profiles" underline="hover">
                {t("anomalies.form.editWorkHours")}
              </Link>
            </Typography>

            {!customerHasWorkHours && outsideWorkingHours ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t("anomalies.form.workHoursRequired")}
              </Alert>
            ) : null}

            {timeGateSummary ? (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {timeGateSummary}
              </Alert>
            ) : null}
          </Box>
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            id="definition"
            name="definition"
            label={t("anomalies.form.definition")}
            variant="outlined"
            size="small"
            multiline
            rows={4}
            value={definition}
            onChange={(event) => setDefinition(event.target.value)}
          />
        </Grid>

        <Grid size={12}>
          <StringListField
            label={t("anomalies.form.alertKeywords")}
            fieldName="alertKeywords"
            values={alertKeywords}
            onChange={setAlertKeywords}
          />
        </Grid>

        <Grid size={12}>
          <StringListField
            label={t("anomalies.form.correlationFields")}
            fieldName="correlationFields"
            values={correlationFields}
            onChange={setCorrelationFields}
          />
        </Grid>
      </Grid>
      </DrawerFormSection>
    </DrawerForm>
  );
};

export default AnomalyEventDialogBody;
